import { useQuery } from '@apollo/client'
import { QueryHookOptions } from '@apollo/client/react/types/types'
import useNativeSmt from '@swarm/core/hooks/data/useNativeSmt'
import { SecuritiesQuery } from '@swarm/core/queries'
import { big, normalize } from '@swarm/core/shared/utils/helpers/big-helpers'
import { normalizeSecurity } from '@swarm/core/shared/utils/subgraph/staking'
import {
  injectExchangeRate,
  useInjections,
} from '@swarm/core/shared/utils/tokens/injectors'
import {
  ExtendedSecurity,
  NormalizedSecurity,
} from '@swarm/types/normalized-entities/staking'
import {
  SecuritiesQueryVariables,
  Security as SecuritySubgraph,
} from '@swarm/types/subgraph/graphql'
import { useMemo } from 'react'

import useRwaStats from '../useRwaStats'

interface SecuritiesResponse {
  securities: SecuritySubgraph[]
}

const POLL_INTERVAL = 20000

const useSecurities = (
  options?: QueryHookOptions<SecuritiesResponse, SecuritiesQueryVariables>,
) => {
  const { nativeSmt } = useNativeSmt()

  const { data: rwaStats, isLoading: rwaStatsLoading } = useRwaStats()

  const {
    data,
    loading: securitiesLoading,
    refetch: reloadSecurities,
  } = useQuery<SecuritiesResponse>(SecuritiesQuery, {
    fetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    pollInterval: POLL_INTERVAL,
    ...options,
    variables: {
      skip: 0,
      ...options?.variables,
    },
  })

  const normalizedSecurities = useMemo<NormalizedSecurity[]>(
    () => data?.securities.map(normalizeSecurity) || [],
    [data?.securities],
  )

  const securitiesExpanded = useInjections<ExtendedSecurity>(
    normalizedSecurities as NormalizedSecurity[],
    useMemo(() => [injectExchangeRate()], []),
    [
      ({ symbol, smtStaked }) => {
        const stats = rwaStats?.find((t) => t.asset === symbol)

        const marketCap = stats?.total_supply ? big(stats.total_supply) : null
        const volume24hs = stats?.traded_volume_24h
          ? big(stats.traded_volume_24h)
          : null

        return {
          marketCap,
          volume24hs,
          smtStaked: nativeSmt
            ? normalize(smtStaked, nativeSmt.decimals)
            : undefined,
        }
      },
    ],
  )

  console.log(securitiesExpanded, rwaStats)

  return {
    securities: securitiesExpanded || [],
    loading: rwaStatsLoading || securitiesLoading,
    reloadSecurities,
  }
}

export default useSecurities
